import React, { FC, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import { Avatar, EAvatarSize } from '../../../../../Common/Avatar/Avatar';
import { fontSizeAndHeight } from '../../../../../../styleHelpers/fontSizes';
import { colorStack } from '../../../../../../styleHelpers/colors';
import { CommonButtonsComponent } from '../../../../../Profiles/Common/CommonButtonsComponent';
import { IGroupInformation } from '.';
import { TextComponent } from '../../../../../Common/Inputs/TextComponent';
import { TextArea } from '../../../../../Common/Inputs/TextArea';
import IntlMessage from '../../../../../Common/IntlMessage';

const Wrapper = styled.div`
    width: 100%;
    margin-bottom: 2rem;
    display:flex;
    justify-content: space-between;
`;

const GroupContainer = styled.div`
    display: flex;
    gap: 3%;
    width: 85%;
`;

const NameContainer = styled.div`
    ${fontSizeAndHeight[31]};
    font-weight: 500;
    margin-top: 0.5rem;
    margin-bottom: 1rem;
    color: ${colorStack.content};
`;

const DescriptionContainer = styled.div`
    ${fontSizeAndHeight[16]};
    font-weight: 400;
    color: ${colorStack.content};
`;

const ButtonSectionWrapper = styled.div`
    display:flex;
    flex-direction: column;
    justify-content: space-between;
    > div > div {
        flex-direction: row;
    }
`;

const DatesContainer = styled.div`
    > div {
        display:flex;
        font-style: italic;
        justify-content: flex-end;
        ${fontSizeAndHeight[13]};
        span {
            margin-left: 0.5rem;
            :nth-child(2) {
                font-weight: 500;
            }
        }
    }
`;

const EditGroupInformationsContainer = styled.div`
    gap: 0.5rem;
    display: flex;
    flex-direction: column;
    width: 100%;
    > div {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: .5rem;
    }
    textarea {
        min-height: 157px;
    }
`;

export interface IProps {
    groupData: IGroupInformation
    isEditable?: boolean;
    /** When true, hides edit / save / cancel (read-only group header). */
    readOnly?: boolean;
    cancel();
    save(body: {name: string, description: string})
}

export const GroupInformation: FC<IProps> = (props) => {
    const [editedGroupData, setEditedGroupData] = useState<IGroupInformation>({
        name: props.groupData.name,
        description: props.groupData.description,
        id: props.groupData.id,
        createdAt: props.groupData.createdAt
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [cancelLoading, setCancelLoading] = useState<boolean>(false);
    const [editMode, setEditMode] = useState<boolean>(false);

    const defaultFiltersSet = () => {
        setEditedGroupData({
            name: props.groupData.name,
            description: props.groupData.description,
            id: props.groupData.id,
            createdAt: props.groupData.createdAt
        });
    };

    const changeEditMode = () => {
        setEditMode(state => !state);
    };

    const cancel = () => {
        setCancelLoading(true);
        props.cancel().then(() => {
            setEditMode(false);
            defaultFiltersSet();
            setCancelLoading(false);
        });
    };

    const nameChange = (value: string) => {
        setEditedGroupData({ ...editedGroupData, name: value });
    };

    const descriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditedGroupData({ ...editedGroupData, description: e.target.value });
    };

    const saveProfile = () => {
        setLoading(true);
        const body = {
            name : editedGroupData.name,
            description: editedGroupData.description
        };
        props.save(body).then(() => {
            setEditMode(false);
            setLoading(false);
        });
    };

    return (
        <Wrapper>
            <GroupContainer>
                {!editMode ? (
                    <>
                        <Avatar
                            picture={undefined}
                            firstName={props.groupData.name}
                            lastName={''}
                            id={props.groupData.id}
                            preventRedirection
                            size={EAvatarSize.XLARGE}
                        />
                        <div>
                            <NameContainer>
                                {editedGroupData.name}
                            </NameContainer>
                            <DescriptionContainer>
                                {editedGroupData.description}
                            </DescriptionContainer>
                        </div>
                    </>
                ) : (
                    <>
                        <Avatar
                            picture={undefined}
                            firstName={props.groupData.name}
                            lastName={''}
                            id={props.groupData.id}
                            preventRedirection
                            size={EAvatarSize.XLARGE}
                        />
                        <EditGroupInformationsContainer>
                            <TextComponent
                                onChange={nameChange}
                                value={editedGroupData.name}
                                maxLenght={50}
                            />
                            <TextArea
                                onChange={descriptionChange}
                                value={editedGroupData.description}
                            />
                        </EditGroupInformationsContainer>
                    </>
                )}
            </GroupContainer>
            <ButtonSectionWrapper>
                {!props.readOnly && (
                    <CommonButtonsComponent
                        loading={loading}
                        cancelLoading={cancelLoading}
                        onCancel={cancel}
                        onSave={saveProfile}
                        editMode={editMode}
                        changeEditMode={changeEditMode}
                        profileContextEditMode={editMode}
                        disabledSave={editedGroupData.name?.trim()?.length === 0}
                        disabledEdit={props.isEditable === false}
                        whiteBackground
                    />
                )}
                <DatesContainer>
                    <div>
                        <span>
                            <IntlMessage id="admin.group.information.creationDate" />
                        </span>
                        <span>
                            {moment(editedGroupData.createdAt).format('DD MMM YYYY')}
                        </span>
                    </div>
                </DatesContainer>
            </ButtonSectionWrapper>
        </Wrapper>
    );
};